import { useMemo, useRef, useEffect, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useInitialData from '@/contexts/InitialDataContext/useInitialData';
import extractAgentIdFromPath from '../helpers/extractAgentIdFromPath';
import { UseChatOrganizationResult } from './types';

/**
 * Hook to derive organizationId from URL path and detect organization changes.
 * Returns the current organizationId and a flag indicating if the org has changed.
 */
const useChatOrganization = (): UseChatOrganizationResult => {
    const location = useLocation();
    const { data: initialData } = useInitialData();
    const prevOrganizationIdRef = useRef<string>('');
    const [hasOrgChanged, setHasOrgChanged] = useState(false);

    const organizationId = useMemo(() => {
        const agentIdFromUrl = extractAgentIdFromPath({ pathname: location.pathname });
        return agentIdFromUrl || initialData.superagents?.[0]?.id || '';
    }, [location.pathname, initialData.superagents]);

    // Detect organization changes
    useEffect(() => {
        // Skip on initial mount
        if (!prevOrganizationIdRef.current) {
            prevOrganizationIdRef.current = organizationId;
            return;
        }

        // If organizationId changed, set the flag
        if (prevOrganizationIdRef.current !== organizationId) {
            setHasOrgChanged(true);
            prevOrganizationIdRef.current = organizationId;
        }
    }, [organizationId]);

    const resetOrgChangeFlag = useCallback(() => {
        setHasOrgChanged(false);
    }, []);

    return {
        organizationId,
        hasOrgChanged,
        resetOrgChangeFlag,
    };
};

export default useChatOrganization;
